/**
 * Collapse share-viewer breadcrumb path to fit a narrow header slot.
 *
 * Stages:
 * 1. Full titles when they fit
 * 2. Leading ancestors → "…" while keeping as many trailing full titles as fit
 * 3. Shorten current to first grapheme + "…"; show full title above the path
 * 4. Drop leading "…" ancestors until the path fits (never drop current)
 */

export type BreadcrumbSegment = { uuid: string; title: string };

export type BreadcrumbDisplaySegment = {
  uuid: string;
  label: string;
  ellipsis: boolean;
  isCurrent: boolean;
};

export type BreadcrumbLayout = {
  showTitleAbove: boolean;
  fullCurrentTitle: string;
  segments: BreadcrumbDisplaySegment[];
};

const ELLIPSIS = '...';

export function firstGrapheme(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return ELLIPSIS;
  try {
    const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    const first = seg.segment(trimmed)[Symbol.iterator]().next().value;
    if (first?.segment) return first.segment;
  } catch {
    /* older runtimes */
  }
  return Array.from(trimmed)[0] ?? ELLIPSIS;
}

export function shortenCurrentTitle(title: string): string {
  const g = firstGrapheme(title);
  return g === ELLIPSIS ? ELLIPSIS : `${g}${ELLIPSIS}`;
}

function pathWidth(
  labels: string[],
  measure: (label: string) => number,
  sepWidth: number,
): number {
  if (labels.length === 0) return 0;
  let w = 0;
  for (let i = 0; i < labels.length; i++) {
    if (i > 0) w += sepWidth;
    w += measure(labels[i]!);
  }
  return w;
}

function buildDisplay(
  segments: BreadcrumbSegment[],
  ellipsisCount: number,
  currentLabel: string,
): BreadcrumbDisplaySegment[] {
  const last = segments.length - 1;
  const out: BreadcrumbDisplaySegment[] = [];
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    const isCurrent = i === last;
    if (isCurrent) {
      out.push({
        uuid: seg.uuid,
        label: currentLabel,
        ellipsis: currentLabel === ELLIPSIS || currentLabel.endsWith(ELLIPSIS),
        isCurrent: true,
      });
      continue;
    }
    if (i < ellipsisCount) {
      out.push({
        uuid: seg.uuid,
        label: ELLIPSIS,
        ellipsis: true,
        isCurrent: false,
      });
    } else {
      out.push({
        uuid: seg.uuid,
        label: seg.title,
        ellipsis: false,
        isCurrent: false,
      });
    }
  }
  return out;
}

/**
 * @param availableWidth — px available for the path (between back and info)
 * @param measure — text → width in the path font
 * @param sepWidth — width of "/" separator including margins
 */
export function layoutShareBreadcrumbs(
  segments: BreadcrumbSegment[],
  availableWidth: number,
  measure: (label: string) => number,
  sepWidth: number,
): BreadcrumbLayout {
  if (segments.length === 0) {
    return { showTitleAbove: false, fullCurrentTitle: '', segments: [] };
  }

  const fullCurrentTitle = segments[segments.length - 1]!.title;
  const max = Math.max(0, availableWidth);

  const tryLayout = (
    ellipsisCount: number,
    currentLabel: string,
    showTitleAbove: boolean,
  ): BreadcrumbLayout | null => {
    const display = buildDisplay(segments, ellipsisCount, currentLabel);
    const labels = display.map((d) => d.label);
    if (pathWidth(labels, measure, sepWidth) <= max) {
      return { showTitleAbove, fullCurrentTitle, segments: display };
    }
    return null;
  };

  // Stage 1: full titles, progressively ellipsis leading ancestors
  for (let ellipsisCount = 0; ellipsisCount <= segments.length - 1; ellipsisCount++) {
    const hit = tryLayout(ellipsisCount, fullCurrentTitle, false);
    if (hit) return hit;
  }

  // Stage 2–3: shortened current + drop leading ellipsis ancestors
  const shortCurrent = shortenCurrentTitle(fullCurrentTitle);
  for (let drop = 0; drop <= segments.length - 1; drop++) {
    const kept = segments.slice(drop);
    const display = buildDisplay(kept, kept.length - 1, shortCurrent);
    const labels = display.map((d) => d.label);
    if (pathWidth(labels, measure, sepWidth) <= max) {
      return {
        showTitleAbove: true,
        fullCurrentTitle,
        segments: display,
      };
    }
  }

  // Last resort: current only
  return {
    showTitleAbove: true,
    fullCurrentTitle,
    segments: [
      {
        uuid: segments[segments.length - 1]!.uuid,
        label: shortCurrent,
        ellipsis: true,
        isCurrent: true,
      },
    ],
  };
}
